import type { TFunction } from 'i18next';
import type { MentionItem, MentionType, SelectedMention } from './flora-mention.type';

/**
 * Get the label for a mention type
 * @param {MentionType} type - The mention type
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The label for the mention type
 */
const getMentionTypeLabel = (type: MentionType, t: TFunction): string => {
  switch (type) {
    case 'portfolio':
      return t('common.portfolio');
    case 'project':
      return t('common.project');
    case 'board':
      return t('common.board');
    case 'sprint':
      return t('common.work_period');
    case 'goal':
      return t('common.strategic_goal');
    case 'outcome':
      return t('common.outcome');
    case 'initiative':
      return t('common.initiative');
    case 'epic':
      return t('common.epic');
    case 'dashboard':
      return t('common.dashboard.dashboard');
    case 'repository':
      return t('common.repository');
  }
};

/**
 * Get the pluralized label for a mention type.
 *
 * Resolves to each type's dedicated `common.*` plural translation key rather than
 * appending a literal "s" to the singular label. Naive `+ "s"` produces
 * "Repositorys" for `repository` and is not localizable, so callers rendering a
 * category/group heading (e.g. "Repositories", "Work Periods") must use this.
 *
 * @param {MentionType} type - The mention type
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The pluralized label for the mention type
 */
const getMentionTypeLabelPlural = (type: MentionType, t: TFunction): string => {
  switch (type) {
    case 'portfolio':
      return t('common.portfolios');
    case 'project':
      return t('common.projects');
    case 'board':
      return t('common.boards');
    case 'sprint':
      return t('common.work_periods');
    case 'goal':
      return t('common.strategic_goals');
    case 'outcome':
      return t('common.outcomes');
    case 'initiative':
      return t('common.initiatives');
    case 'epic':
      return t('common.epics');
    case 'dashboard':
      return t('common.dashboard.dashboards');
    case 'repository':
      return t('common.repositories');
  }
};

/**
 * Format a mention for a message
 * @param {MentionItem} mention - The mention to format
 * @returns {string} The formatted mention
 */
const formatMentionForMessage = (mention: MentionItem): string => {
  switch (mention.type) {
    case 'sprint':
      return `@work_period:${mention.id}[${mention.name}]@parent:board:${mention.parentId}[${mention.parentName}]`;
    default:
      return `@${mention.type}:${mention.id}[${mention.name}]`;
  }
};

/**
 * Get the color for a mention type
 * @param {MentionType} type - The mention type
 * @returns {string} The color for the mention type
 */
const getMentionTypeColor = (type: MentionType): string => {
  switch (type) {
    case 'portfolio':
      return '#9154E2'; // purple
    case 'project':
      return '#2B70E4'; // blue
    case 'board':
      return '#339F00'; // darkGreen
    case 'sprint':
      return '#FF671D'; // orange
    case 'goal':
      return '#E91E63'; // pink
    case 'outcome':
      return '#00BCD4'; // cyan
    case 'initiative':
      return '#D4890A'; // amber
    case 'epic':
      return '#7B1FA2'; // deep purple
    case 'dashboard':
      return '#537FF1'; // indigo-blue
    case 'repository':
      return '#0F766E'; // teal
  }
};

type MentionSegment =
  | { type: 'text'; value: string }
  | { type: 'mention'; mentionType: MentionType; id: string; name: string };

const SERIALIZED_TYPE_TO_MENTION_TYPE: Record<string, MentionType> = {
  portfolio: 'portfolio',
  project: 'project',
  board: 'board',
  work_period: 'sprint',
  goal: 'goal',
  outcome: 'outcome',
  initiative: 'initiative',
  epic: 'epic',
  repository: 'repository',
};

/**
 * Parse a message string and extract mention segments
 * @param {string} message - The raw message string
 * @returns {MentionSegment[]} Array of text and mention segments
 */
const parseMentions = (message: string): MentionSegment[] => {
  const mentionRegex =
    /@(portfolio|project|board|work_period|goal|outcome|initiative|epic|repository):([^[]+)\[([^\]]+)\]/g;
  const segments: MentionSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(message)) !== null) {
    // Add preceding text
    if (match.index > lastIndex) {
      let textBefore = message.slice(lastIndex, match.index);
      // Strip trailing @parent:board:...[...] from previous sprint mention
      textBefore = textBefore.replace(/^@parent:board:[^[]*\[[^\]]*\]/, '');
      if (textBefore) {
        segments.push({ type: 'text', value: textBefore });
      }
    }

    const serializedType = match[1];
    const mentionType = SERIALIZED_TYPE_TO_MENTION_TYPE[serializedType];
    if (mentionType) {
      segments.push({ type: 'mention', mentionType, id: match[2], name: match[3] });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text (strip any trailing @parent:... suffix)
  if (lastIndex < message.length) {
    let remaining = message.slice(lastIndex);
    remaining = remaining.replace(/^@parent:board:[^[]*\[[^\]]*\]/, '');
    if (remaining) {
      segments.push({ type: 'text', value: remaining });
    }
  }

  return segments;
};

/**
 * Substitutes `[@Name]` placeholders in a prompt with their canonical wire-format strings.
 *
 * Replaces from the rightmost placeholder first so earlier replacements don't shift indices of later ones.
 *
 * @param {string} text - The raw prompt that still contains `[@Name]` placeholders
 * @param {SelectedMention[]} mentions - The mentions the user picked while typing
 * @returns {string} The prompt with each placeholder substituted for `formatMentionForMessage(mention)`; returned unchanged when mentions is empty
 */
const formatPromptWithMentions = (text: string, mentions: SelectedMention[]): string => {
  if (mentions.length === 0) return text;
  const sorted = [...mentions].sort((a, b) => b.startIndex - a.startIndex);
  let formatted = text;
  for (const mention of sorted) {
    const placeholder = `[@${mention.name}]`;
    const start = mention.startIndex;
    const end = start + placeholder.length;
    if (formatted.slice(start, end) !== placeholder) continue;
    formatted = `${formatted.slice(0, start)}${formatMentionForMessage(mention)}${formatted.slice(end)}`;
  }
  return formatted;
};

export {
  formatMentionForMessage,
  formatPromptWithMentions,
  getMentionTypeColor,
  getMentionTypeLabel,
  getMentionTypeLabelPlural,
  parseMentions,
};
